import { ArrowUpRight } from 'lucide-react';
import { useId, useMemo, useSyncExternalStore } from 'react';
import { StoneSkeleton } from '@/components/media';
import { CtaBand } from '@/components/layout/CtaBand';
import { HERO_ART } from '@/assets/heroes';
import { PageHero } from '@/components/layout/PageHero';
import { HeroArt } from '@/components/media/HeroArt';
import { AppLink } from '@/components/ui/AppLink';
import { buttonClasses } from '@/components/ui/button-classes';
import { Container } from '@/components/ui/Container';
import { Section } from '@/components/ui/Section';
import { useT } from '@/lib/i18n';
import { StackReveal } from '@/motion/Reveal';
import { Adjustments } from './Adjustments';
import { SceneSwitcher } from './SceneSwitcher';
import { ShareActions } from './ShareActions';
import { StageSection } from './StageSection';
import { StonePicker } from './StonePicker';
import { useVisualizer } from './useVisualizer';
import type { VisualizerModel } from './useVisualizer';
import './visualizer.css';

const noopSubscribe = (): (() => void) => () => undefined;

/**
 * "See it in your space" (DESIGN_SYSTEM §4.4): the page hero, the tool, a short "how it works" and the
 * closing call to action.
 *
 * The tool needs a browser (layout measurement, pointer dragging, the clipboard), so the server — and
 * the pre-rendered HTML — carry the hero, the explanation and a link to the shop, and the tool mounts
 * once the page is running. Nothing here reads `window` while rendering.
 *
 * Notes for later (not built, on purpose):
 *   • Real photography: every scene has a `photo` slot (`scenes/types.ts`) — a photograph, the corners
 *     of each surface in it and an optional cut-out of what stands in front. See `ScenePhotoSlot`.
 *   • AR / WebXR: a future "view it in your room" path would load a glTF slab with the product's
 *     texture and place it with a WebXR hit-test; it would sit next to this tool, not replace it.
 */
export function VisualizerPage() {
  const model = useVisualizer();
  const t = useT();
  const { copy } = model;
  const interactive = useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false,
  );

  return (
    <div className="viz-page">
      <PageHero
        eyebrow={copy.hero.eyebrow}
        title={copy.hero.title}
        lead={copy.hero.lead}
        breadcrumbs={[{ label: t('nav.home'), to: '/' }, { label: t('nav.visualizer') }]}
        className="film-hero film-hero--medium on-dark"
        media={<HeroArt art={HERO_ART.visualizer} focusY={20} />}
      />

      {interactive ? <Tool model={model} /> : <ToolPlaceholder model={model} />}

      <Section tone="surface" eyebrow={copy.how.eyebrow} title={copy.how.title}>
        <StackReveal as="ol" className="viz-steps">
          {copy.how.steps.map((step, index) => (
            <li key={step.title} className="viz-step">
              <span className="viz-step__number" aria-hidden="true">
                {String(index + 1).padStart(2, '0')}
              </span>
              <h3 className="viz-step__title t-lead">{step.title}</h3>
              <p className="t-body text-fg-muted">{step.body}</p>
            </li>
          ))}
        </StackReveal>
        <p className="viz-how-note t-small text-fg-muted">{copy.how.note}</p>
        <AppLink
          to="/shop"
          className={buttonClasses({ variant: 'outline', className: 'viz-how-link' })}
        >
          {copy.how.shop}
        </AppLink>
      </Section>

      <CtaBand
        tone="bg"
        title={copy.cta.title}
        body={copy.cta.body}
        {...(model.enquiry ? { product: model.enquiry.slug } : {})}
      />
    </div>
  );
}

/** What the server (and a visitor without JavaScript) gets in place of the tool. */
function ToolPlaceholder({ model }: { model: VisualizerModel }) {
  const { copy } = model;
  return (
    <section className="section viz-tool" data-tone="bg" aria-label={copy.toolTitle}>
      <Container>
        <div className="viz-fallback">
          <StoneSkeleton className="viz-fallback__box" />
          <noscript>
            <p className="t-body text-fg-muted">{copy.noscript.body}</p>
            <AppLink to="/shop" className={buttonClasses({ variant: 'primary' })}>
              {copy.noscript.link}
            </AppLink>
          </noscript>
        </div>
      </Container>
    </section>
  );
}

function Tool({ model }: { model: VisualizerModel }) {
  const { copy, selection } = model;
  const toolId = useId();
  const sceneId = useId();
  const stoneId = useId();
  const adjustId = useId();
  const actionsId = useId();

  const detail = selection.detail;
  const colour = detail?.color ? detail.color.label : null;
  const origin = detail?.origin ? detail.origin.label : null;
  const facts = [colour, origin].filter((fact): fact is string => Boolean(fact)).join(' · ');
  const sceneNames = useMemo(
    () => ({
      kitchen: copy.scene.options.kitchen.name,
      floor: copy.scene.options.floor.name,
      wall: copy.scene.options.wall.name,
      staircase: copy.scene.options.staircase.name,
    }),
    [copy],
  );

  return (
    <section className="section viz-tool" data-tone="bg" aria-labelledby={toolId}>
      <Container>
        <h2 id={toolId} className="sr-only">
          {copy.toolTitle}
        </h2>

        <div className="viz-layout">
          <div className="viz-layout__stage">
            <StageSection
              scene={model.scene}
              status={model.stage.status}
              texture={model.stage.texture}
              scale={model.scale}
              rotate={model.rotate}
              seams={model.seams}
              committed={model.compare.committed}
              onCommit={model.compare.commit}
              label={model.stage.label}
              stoneChip={model.shownName || copy.stage.loading}
              copy={copy}
            />

            <div className="viz-caption">
              <div className="viz-caption__text">
                <p className="viz-eyebrow">{copy.stone.selected}</p>
                <p className="viz-caption__name">
                  {detail ? detail.name : model.shownName || copy.stage.loading}
                </p>
                {facts ? <p className="t-small text-fg-muted">{facts}</p> : null}
              </div>
              {detail ? (
                <AppLink to={`/shop/${detail.slug}`} className="viz-caption__link">
                  {copy.stone.details}
                  <ArrowUpRight size={16} aria-hidden="true" />
                </AppLink>
              ) : null}
            </div>
            <p className="viz-hint t-small text-fg-muted">{copy.compare.hint}</p>
          </div>

          <div className="viz-layout__rail">
            <section className="viz-panel" aria-labelledby={sceneId}>
              <h3 id={sceneId} className="viz-panel__title">
                {copy.scene.heading}
              </h3>
              <SceneSwitcher
                value={model.scene.id}
                onChange={model.selectScene}
                labelledBy={sceneId}
                tint={model.tint}
                names={sceneNames}
              />
            </section>

            <section className="viz-panel" aria-labelledby={stoneId}>
              <h3 id={stoneId} className="viz-panel__title">
                {copy.stone.heading}
              </h3>
              {selection.unknownRequested ? (
                <p className="viz-banner t-small">{copy.stone.unknown}</p>
              ) : null}
              <StonePicker
                selected={selection.slug}
                onSelect={model.selectStone}
                onAnnounce={model.announce}
                copy={copy.stone}
                announce={copy.announce}
                labelledBy={stoneId}
              />
            </section>

            <section className="viz-panel" aria-labelledby={adjustId}>
              <h3 id={adjustId} className="viz-panel__title">
                {copy.adjust.heading}
              </h3>
              <Adjustments
                scale={model.scale}
                onScale={model.setScale}
                rotate={model.rotate}
                onRotate={model.setRotate}
                seams={model.seams}
                seamsAvailable={model.scene.seams}
                onSeams={model.setSeams}
                dirty={model.dirty}
                onReset={model.reset}
                copy={copy.adjust}
              />
            </section>

            <section className="viz-panel" aria-labelledby={actionsId}>
              <h3 id={actionsId} className="viz-panel__title">
                {copy.actions.heading}
              </h3>
              <ShareActions
                product={model.enquiry}
                link={model.link}
                copy={copy.actions}
                copiedMessage={copy.announce.copied}
                onAnnounce={model.announce}
              />
              <p className="viz-note-text t-small text-fg-muted">{copy.note}</p>
            </section>
          </div>
        </div>

        {/* the one polite live region of the page: scene and stone changes, "more loaded", "link copied" */}
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          <span key={model.announcement.n}>{model.announcement.text}</span>
        </div>
        {model.probe}
      </Container>
    </section>
  );
}
